import { Component, OnInit } from '@angular/core';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import {
  IndexSummary,
  IndexSummaryDetail,
} from '../../../../models/api/view/claim/documentation/index-system/index-summary';
import { IndexSystemMapper } from '../../../../utilities/index-system-mapper';
import { BaseComponent } from '../../../base/base.component';
import { AddressLabelComponent } from '../../../core/labels/address-label.component';
import { SsnLabelComponent } from '../../../core/labels/ssn-label.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

interface IndexSummaryDisplay extends IndexSummary {
  date: Date | null;
  report: string;
  searchDisplay: string;
  type: string;
  statusDisplay: string;
  result: string;
  matchReasonDisplay: string;
  reportType: string;
  reportDate: Date | null;
}

@Component({
  selector: 'app-index-system-summary',
  imports: [
    ColumnComponent,
    TableComponent,
    DetailItemComponent,
    DetailPanelComponent,
    AddressLabelComponent,
    SsnLabelComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'" [error]="error">
      <ng-template #loaded>
        <app-table
          [dataSource]="summaryData"
          [pageSize]="5"
          [scrollable]="false"
          [expandable]="true"
          [hideExpandAll]="true"
          [canExpand]="canExpand"
          dataKey="indexId"
          recordLabel="Index Summary"
          recordLabelPlural="Index Summaries"
          (rowExpand)="onRowExpand($event)"
        >
          <app-column name="date" label="Date" width="10%" format="d" />
          <app-column name="report" label="Report" width="15%" />
          <app-column name="searchDisplay" label="Search" width="10%" />
          <app-column name="type" label="Type" width="10%" />
          <app-column name="statusDisplay" label="Status" width="10%" />
          <app-column name="result" label="Result" width="15%" />
          <app-column
            name="matchReasonDisplay"
            label="Match Reason"
            width="25%"
          />

          <ng-template #expand let-data>
            <app-loading-panel
              [state]="detailCache.has(data.indexId) ? 'Loaded' : 'Loading'"
              [loadingHeight]="100"
            >
              <ng-template #loaded>
                <div class="tw-flex tw-gap-8">
                  <div class="tw-basis-1/4">
                    <app-detail-panel
                      [dataSource]="data"
                      [showIcons]="false"
                      [showEmpty]="false"
                    >
                      <app-detail-item label="Report Type" name="reportType" />
                      <app-detail-item
                        label="Report Date"
                        name="reportDate"
                        format="d"
                      />
                      <app-detail-item
                        label="Customer Code"
                        name="customerCode"
                      />
                    </app-detail-panel>
                  </div>
                  <div class="tw-basis-1/4">
                    <app-detail-panel
                      [dataSource]="data"
                      [showIcons]="false"
                      [showEmpty]="false"
                    >
                      <app-detail-item label="Name" name="name" />
                      <app-detail-item label="SSN" name="ssn" labelCase="none">
                        <ng-template let-data #value>
                          <app-ssn-label
                            [value]="data.ssn"
                            [canUnmaskSsn]="data.isUnmaskSsn"
                          />
                        </ng-template>
                      </app-detail-item>
                      <app-detail-item label="Address" name="addr1">
                        <ng-template let-data #value>
                          <app-address-label
                            [street1]="data.addr1"
                            [street2]="data.addr2"
                            [city]="data.city"
                            [state]="data.state"
                            [postalCode]="data.zip"
                            [country]="data.country"
                            [leftAlign]="true"
                          />
                        </ng-template>
                      </app-detail-item>
                    </app-detail-panel>
                  </div>
                </div>
              </ng-template>
            </app-loading-panel>
          </ng-template>
        </app-table>
      </ng-template>
    </app-loading-panel>
  `,
  styles: [],
})
export class IndexSystemSummaryComponent
  extends BaseComponent
  implements OnInit
{
  claim: ClaimRow;
  summaryData: IndexSummaryDisplay[] = [];
  detailCache = new Map<number, IndexSummaryDetail>();
  loadingDetails = new Set<number>();

  loading = false;
  error: any = null;

  // Legacy: no IndexID → no detail panel (typical for Pending)
  canExpand = (row: IndexSummaryDisplay): boolean => row.indexId != null;

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.claim = this.view.currentClaim();
    await this.loadSummary();
  }

  @HandleLoadingState()
  async loadSummary(): Promise<void> {
    const rawData = await this.api.indexSystem.getSummary({
      recordSource: this.claim.recordSource,
      recordId: this.claim.recordId,
    });

    this.summaryData = rawData.map((row) => {
      const mapped = IndexSystemMapper.mapSummaryRow(row);
      return {
        ...row,
        date: row.indexDate ? new Date(row.indexDate) : null,
        report: mapped.indexType,
        searchDisplay: mapped.search,
        type: mapped.requestType,
        statusDisplay: mapped.status,
        result: mapped.result,
        matchReasonDisplay: mapped.matchReason,
        reportType: mapped.indexType,
        reportDate: row.indexDate ? new Date(row.indexDate) : null,
      };
    });
  }

  @HandleLoadingState({
    loadingProperty: 'detailLoading',
    errorProperty: 'rowExpandError',
  })
  async onRowExpand(event: { data: IndexSummaryDisplay }): Promise<void> {
    const row = event.data;
    if (
      !row.indexId ||
      this.detailCache.has(row.indexId) ||
      this.loadingDetails.has(row.indexId)
    ) {
      return;
    }

    this.loadingDetails.add(row.indexId);

    try {
      const detail = await this.api.indexSystem.getDetail(
        {
          recordSource: this.claim.recordSource,
          recordId: this.claim.recordId,
        },
        row.indexId,
      );

      if (detail) {
        this.detailCache.set(row.indexId, detail);
        Object.assign(row, detail);
      } else {
        // Stop loading when detail is unavailable (Legacy hides the panel)
        this.detailCache.set(row.indexId, {} as IndexSummaryDetail);
      }
    } finally {
      this.loadingDetails.delete(row.indexId);
    }
  }
}
